import { OpenRouterConfig } from '../components/OpenRouterConfig';
import { Language } from '../hooks/useLanguage';

export type ImprovementType = 'grammar' | 'clarity' | 'structure' | 'tone' | 'conciseness' | 'translate' | 'general';

export interface TextImprovementResult {
  originalText: string;
  improvedText: string;
  improvementType: ImprovementType;
  changes: TextChange[];
  explanation?: string;
}

export interface TextChange {
  type: 'grammar' | 'style' | 'structure' | 'tone' | 'clarity';
  description: string;
  before: string;
  after: string;
}

export class TextImprovementService {
  private config: OpenRouterConfig;

  constructor(config: OpenRouterConfig) {
    this.config = config;
  }

  async improveText(
    text: string, 
    improvementType: ImprovementType = 'general',
    language: Language = 'en'
  ): Promise<TextImprovementResult> {
    // Para tradução, usamos um prompt especializado
    if (improvementType === 'translate') {
      return this.translateText(text, language);
    }
    const prompt = this.generateImprovementPrompt(text, improvementType, language);

    try {
      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.config.apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Prompt Editor - Text Improvement - Aldeia Viva'
        },
        body: JSON.stringify({
          model: this.config.model || 'anthropic/claude-3.5-sonnet',
          messages: [{ role: 'user', content: prompt }],
          max_tokens: this.config.maxTokens || 1000,
          temperature: 0.3, // Lower temperature for more consistent improvements
        })
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      const result = data.choices?.[0]?.message?.content;

      if (!result) {
        throw new Error('No improvement result received from API');
      }

      return this.parseImprovementResult(text, result, improvementType);
    } catch (error) {
      console.error('Error improving text:', error);
      // Return a generic error that will be handled by the component with i18n
      throw error;
    }
  }

  private async translateText(
    text: string, 
    targetLanguage: Language
  ): Promise<TextImprovementResult> {
    const isPortuguese = targetLanguage === 'pt-BR';
    
    const prompt = this.generateTranslationPrompt(text, targetLanguage);

    try {
      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.config.apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Prompt Editor - Text Translation - Aldeia Viva'
        },
        body: JSON.stringify({
          model: this.config.model || 'anthropic/claude-3.5-sonnet',
          messages: [{ role: 'user', content: prompt }],
          max_tokens: this.config.maxTokens || 1000,
          temperature: 0.1, // Muito baixa para traduções mais consistentes
        })
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      const result = data.choices?.[0]?.message?.content;

      if (!result) {
        throw new Error('No translation result received from API');
      }

      return this.parseTranslationResult(text, result);
    } catch (error) {
      console.error('Error translating text:', error);
      throw error;
    }
  }

  private generateTranslationPrompt(text: string, targetLanguage: Language): string {
    const isPortuguese = targetLanguage === 'pt-BR';
    
    const targetLangName = isPortuguese ? 'português brasileiro' : 'English';
    const targetLangInstruction = isPortuguese 
      ? 'FUNDAMENTAL: Responda SEMPRE em português brasileiro.' 
      : 'FUNDAMENTAL: ALWAYS respond in English.';

    return `You are a professional translator specializing in ${isPortuguese ? 'Portuguese' : 'English'} translation.

${targetLangInstruction}

TASK: Translate the following text to ${targetLangName}.

REQUIREMENTS:
- Maintain the original meaning, tone, and style perfectly
- Use natural, fluent language appropriate for the target audience
- Preserve formatting, structure, and any technical terms appropriately
- If the text is already in ${targetLangName}, respond with the original text unchanged
- Focus on clarity and readability in the target language

TEXT TO TRANSLATE:
"${text}"

RESPONSE FORMAT:
Return ONLY the translated text, without any additional comments or explanations.

${isPortuguese ? 'IMPORTANTE: Responda apenas com a tradução, sem comentários adicionais.' : 'IMPORTANT: Respond only with the translation, without additional comments.'}`;
  }

  private parseTranslationResult(originalText: string, result: string): TextImprovementResult {
    // Para tradução, o resultado é mais direto
    const translatedText = result.trim();
    
    return {
      originalText,
      improvedText: translatedText,
      improvementType: 'translate',
      changes: [{
        type: 'style',
        description: 'Text translated',
        before: originalText.substring(0, 50) + (originalText.length > 50 ? '...' : ''),
        after: translatedText.substring(0, 50) + (translatedText.length > 50 ? '...' : '')
      }],
      explanation: 'Text has been translated to the selected interface language.'
    };
  }

  private generateImprovementPrompt(
    text: string, 
    improvementType: ImprovementType,
    language: Language
  ): string {
    const isPortuguese = language === 'pt-BR';
    
    const baseInstruction = isPortuguese
      ? 'Você é um especialista em redação e edição de textos em português brasileiro.'
      : 'You are an expert in writing and text editing in English.';

    const languageInstruction = isPortuguese
      ? 'FUNDAMENTAL: Responda SEMPRE em português brasileiro.'
      : 'FUNDAMENTAL: ALWAYS respond in English.';

    const improvementInstructions = {
      general: isPortuguese
        ? 'Melhore o texto de forma geral, focando em clareza, gramática, estrutura e fluidez.'
        : 'Improve the text in general, focusing on clarity, grammar, structure, and flow.',
      
      grammar: isPortuguese
        ? 'Corrija apenas erros gramaticais, de ortografia e pontuação, mantendo o estilo original.'
        : 'Only fix grammatical, spelling, and punctuation errors while maintaining the original style.',
      
      clarity: isPortuguese
        ? 'Torne o texto mais claro e compreensível, simplificando frases complexas e removendo ambiguidades.'
        : 'Make the text clearer and more understandable, simplifying complex sentences and removing ambiguities.',
      
      structure: isPortuguese
        ? 'Melhore a estrutura e organização do texto, reorganizando parágrafos e ideias para melhor fluxo.'
        : 'Improve the structure and organization of the text, reorganizing paragraphs and ideas for better flow.',
      
      tone: isPortuguese
        ? 'Ajuste o tom do texto para torná-lo mais profissional, envolvente e adequado ao público-alvo.'
        : 'Adjust the tone of the text to make it more professional, engaging, and appropriate for the target audience.',
      
      conciseness: isPortuguese
        ? 'Torne o texto mais conciso, removendo redundâncias e palavras desnecessárias, mantendo o significado.'
        : 'Make the text more concise, removing redundancies and unnecessary words while maintaining meaning.',
      
      translate: isPortuguese
        ? 'Traduza todo o texto para português brasileiro claro e natural, mantendo perfeitamente o tom, significado, contexto e estrutura originais. Se já estiver em português, mantenha-o inalterado.'
        : 'Translate all text to clear and natural English, perfectly maintaining the original tone, meaning, context and structure. If already in English, keep it unchanged.'
    };

    const outputFormat = isPortuguese
      ? `
FORMATO DE RESPOSTA OBRIGATÓRIO:
==TEXTO_MELHORADO==
[texto melhorado aqui]

==EXPLICAÇÃO==
[breve explicação das principais mudanças feitas]

==MUDANÇAS==
[lista das principais alterações, uma por linha, no formato: "Antes: [texto original] → Depois: [texto modificado]"]`
      : `
MANDATORY RESPONSE FORMAT:
==IMPROVED_TEXT==
[improved text here]

==EXPLANATION==
[brief explanation of main changes made]

==CHANGES==
[list of main changes, one per line, in format: "Before: [original text] → After: [modified text]"]`;

    return `${baseInstruction}

${languageInstruction}

TAREFA: ${improvementInstructions[improvementType]}

TEXTO ORIGINAL:
"${text}"

${outputFormat}

IMPORTANTE: Mantenha o contexto e significado originais. Se o texto já estiver bem escrito, faça apenas melhorias sutis.`;
  }

  private parseImprovementResult(
    originalText: string,
    result: string,
    improvementType: ImprovementType
  ): TextImprovementResult {
    try {
      // Parse the structured response
      const improvedTextMatch = result.match(/==(?:TEXTO_MELHORADO|IMPROVED_TEXT)==\s*([\s\S]*?)\s*==(?:EXPLICAÇÃO|EXPLANATION)==/);
      const explanationMatch = result.match(/==(?:EXPLICAÇÃO|EXPLANATION)==\s*([\s\S]*?)\s*==(?:MUDANÇAS|CHANGES)==/);
      const changesMatch = result.match(/==(?:MUDANÇAS|CHANGES)==\s*([\s\S]*?)$/);

      const improvedText = improvedTextMatch?.[1]?.trim() || result.trim();
      const explanation = explanationMatch?.[1]?.trim();
      const changesText = changesMatch?.[1]?.trim();

      // Parse changes
      const changes: TextChange[] = [];
      if (changesText) {
        const changeLines = changesText.split('\n').filter(line => line.trim());
        changeLines.forEach(line => {
          const match = line.match(/(?:Antes|Before):\s*(.*?)\s*→\s*(?:Depois|After):\s*(.*)/);
          if (match) {
            changes.push({
              type: this.getChangeType(improvementType),
              description: `${match[1]} → ${match[2]}`,
              before: match[1].trim(),
              after: match[2].trim()
            });
          }
        });
      }

      return {
        originalText,
        improvedText,
        improvementType,
        changes,
        explanation
      };
    } catch (error) {
      // Fallback: return the result as improved text
      console.warn('Failed to parse improvement result, using fallback');
      return {
        originalText,
        improvedText: result.trim(),
        improvementType,
        changes: []
      };
    }
  }

  private getChangeType(improvementType: ImprovementType): TextChange['type'] {
    switch (improvementType) {
      case 'grammar': return 'grammar';
      case 'clarity': return 'clarity';
      case 'structure': return 'structure';
      case 'tone': return 'tone';
      case 'translate': return 'style'; // Tratamos tradução como mudança de estilo
      case 'conciseness':
      case 'general':
      default: return 'style';
    }
  }
}